import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getDadStageDisplayLabel } from '../tabs/members/memberConstants';

const readErrorResponse = async (response) => {
  let payload = null;
  let rawText = '';

  try {
    rawText = await response.text();
  } catch {
    rawText = '';
  }

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = null;
    }
  }

  const detail = payload?.detail || payload?.message || rawText || response.statusText;
  if (typeof detail === 'string') {
    const compact = detail.replace(/\s+/g, ' ').trim();
    return compact ? `Update failed (${response.status}): ${compact.slice(0, 300)}` : `Update failed (${response.status})`;
  }

  return `Update failed (${response.status})`;
};

const createEmptyEditMember = () => ({
  name: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  country: '',
  marriage_status: '',
  married_years: '',
  children_summary: '',
  current_pressure_notes: '',
  tier: 'member',
  status: 'active',
  billing_cycle: 'monthly',
  cohorts: '',
  bio: '',
  focusPreferences: [],
  relationship_stage: 'prospect',
  campaign_type: '',
  source: '',
  warmth: '',
  children_age_range: '',
  biggest_pressure_area: '',
  notes: '',
});

export function useMembersAdminState({ adminFetch }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [memberToHardDelete, setMemberToHardDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [reinstatingId, setReinstatingId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [savingRoles, setSavingRoles] = useState(false);
  const [memberRoles, setMemberRoles] = useState({ primary_tier: 'member', additional_roles: [] });
  const [showRemoved, setShowRemoved] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editMember, setEditMember] = useState(createEmptyEditMember);
  const [savingEdit, setSavingEdit] = useState(false);
  const [importingDads, setImportingDads] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [movingToGuidePartnerId, setMovingToGuidePartnerId] = useState(null);
  const editMemberPhotoInputRef = useRef(null);
  const hasLoadedMembersRef = useRef(false);

  const openEditModal = useCallback((member) => {
    setEditingMember(member);
    setEditMember({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      city: member.city || '',
      state: member.state || '',
      country: member.country || '',
      marriage_status: member.marriage_status || '',
      married_years: member.married_years || '',
      children_summary: member.children_summary || '',
      current_pressure_notes: member.current_pressure_notes || '',
      tier: member.tier || 'member',
      status: member.status || 'active',
      billing_cycle: member.billing_cycle || 'monthly',
      cohorts: Array.isArray(member.cohorts) ? member.cohorts.join(', ') : (member.cohorts || ''),
      bio: member.bio || '',
      focusPreferences: Array.isArray(member.focus_preferences) ? member.focus_preferences : [],
      relationship_stage: member.relationship_stage || ((member.tier || '') === 'prospect' ? 'prospect' : ''),
      campaign_type: member.campaign_type || '',
      source: member.source || '',
      warmth: member.warmth || '',
      children_age_range: member.children_age_range || '',
      biggest_pressure_area: member.biggest_pressure_area || '',
      notes: member.notes || '',
    });
    setShowEditModal(true);
  }, []);

  const handleEditMemberPhotoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editingMember?.user_id) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be less than 3MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await adminFetch(`/admin/members/${editingMember.user_id}/photo`, {
        method: 'POST',
        body: form,
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.user) {
          setEditingMember((prev) => ({ ...prev, picture: data.user.picture }));
        }
        toast.success('Profile photo updated');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (event.target) event.target.value = '';
    }
  }, [adminFetch, editingMember?.user_id]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await adminFetch('/admin/members');
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        hasLoadedMembersRef.current = true;
        setMembers(list);
      } else {
        console.error('Failed to fetch members:', response.status);
        if (!hasLoadedMembersRef.current) {
          setMembers([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
      if (!hasLoadedMembersRef.current) {
        setMembers([]);
      }
    } finally {
      setMembersLoading(false);
    }
  }, [adminFetch]);

  const openRolesModal = useCallback((member) => {
    setSelectedMember(member);
    const primaryTier = member.tier || 'member';

    setMemberRoles({
      primary_tier: primaryTier,
      additional_roles: member.additional_roles || [],
    });
    setShowRolesModal(true);
  }, []);

  const toggleAdditionalRole = useCallback((role) => {
    setMemberRoles((prev) => {
      if (role === prev.primary_tier) return prev;
      const roles = prev.additional_roles.includes(role)
        ? prev.additional_roles.filter((item) => item !== role)
        : [...prev.additional_roles, role];
      return { ...prev, additional_roles: roles };
    });
  }, []);

  const handleSaveRoles = useCallback(async () => {
    if (!selectedMember?.user_id) {
      toast.error('Missing user_id for selected member.');
      return;
    }

    setSavingRoles(true);
    try {
      const payload = {
        primary_tier: memberRoles.primary_tier,
        additional_roles: memberRoles.additional_roles || [],
      };

      const response = await adminFetch(`/admin/members/${selectedMember.user_id}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = response.ok ? await response.json().catch(() => ({})) : null;
      if (response.ok) {
        toast.success('Roles updated');
        setShowRolesModal(false);
        await fetchMembers();
      } else {
        const error = data?.detail || data?.message || response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
      }
    } catch (error) {
      console.error('handleSaveRoles error:', error);
      toast.error(error?.message || 'Failed to update roles');
    } finally {
      setSavingRoles(false);
    }
  }, [adminFetch, fetchMembers, memberRoles, selectedMember?.user_id]);

  const openRemoveModal = useCallback((member) => {
    setMemberToRemove(member);
    setRemoveReason('');
    setShowRemoveModal(true);
  }, []);

  const openHardDeleteModal = useCallback((member) => {
    setMemberToHardDelete(member);
    setShowHardDeleteModal(true);
  }, []);

  const handleRemoveMember = useCallback(async () => {
    const reason = removeReason?.trim();
    if (!reason || !memberToRemove) return;

    setRemoving(true);
    try {
      const response = await adminFetch(`/admin/members/${memberToRemove.user_id}/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, new_status: 'former' }),
      });
      const data = response.ok ? await response.json().catch(() => ({})) : null;

      if (response.ok) {
        toast.success('Dad removed');
        setShowRemoveModal(false);
        setMemberToRemove(null);
        setRemoveReason('');
        fetchMembers();
      } else {
        const error = data?.detail ?? data?.message ?? response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to remove member');
    } finally {
      setRemoving(false);
    }
  }, [adminFetch, fetchMembers, memberToRemove, removeReason]);

  const handleHardDeleteMember = useCallback(async () => {
    if (!memberToHardDelete) return;

    setHardDeleting(true);
    try {
      const response = await adminFetch(`/admin/members/${memberToHardDelete.user_id}`, {
        method: 'DELETE',
      });
      const data = response.ok ? await response.json().catch(() => ({})) : null;

      if (response.ok) {
        toast.success('Dad permanently deleted');
        setShowHardDeleteModal(false);
        setMemberToHardDelete(null);
        fetchMembers();
      } else {
        const error = data?.detail ?? data?.message ?? response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to permanently delete member');
    } finally {
      setHardDeleting(false);
    }
  }, [adminFetch, fetchMembers, memberToHardDelete]);

  const handleReinstateMember = useCallback(async (member) => {
    if (!member?.user_id) return;

    setReinstatingId(member.user_id);
    try {
      const response = await adminFetch(`/admin/members/${member.user_id}/reinstate`, {
        method: 'POST',
      });
      const data = response.ok ? await response.json().catch(() => ({})) : null;

      if (response.ok) {
        toast.success(`${member.name || 'Dad'} reinstated`);
        fetchMembers();
      } else {
        const error = data?.detail ?? data?.message ?? response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to reinstate member');
    } finally {
      setReinstatingId(null);
    }
  }, [adminFetch, fetchMembers]);

  const handleSaveMemberEdit = useCallback(async () => {
    if (!editingMember?.user_id) {
      toast.error('Missing user_id');
      return;
    }

    setSavingEdit(true);
    try {
      const payload = {
        name: editMember.name?.trim() || '',
        email: editMember.email?.trim() || '',
        phone: editMember.phone?.trim() || '',
        city: editMember.city?.trim() || null,
        state: editMember.state?.trim() || null,
        country: editMember.country?.trim() || null,
        marriage_status: editMember.marriage_status?.trim() || null,
        married_years: editMember.married_years?.trim() || null,
        children_summary: editMember.children_summary?.trim() || null,
        current_pressure_notes: editMember.current_pressure_notes?.trim() || null,
        tier: editMember.tier,
        status: editMember.status,
        billing_cycle: editMember.billing_cycle,
        cohorts: editMember.cohorts?.trim()
          ? editMember.cohorts.split(',').map((value) => value.trim()).filter(Boolean)
          : [],
        bio: editMember.bio?.trim() ?? '',
        focus_preferences: editMember.focusPreferences ?? [],
        relationship_stage: editMember.relationship_stage || null,
        campaign_type: editMember.campaign_type || null,
        source: editMember.source || null,
        warmth: editMember.warmth || null,
        children_age_range: editMember.children_age_range || null,
        biggest_pressure_area: editMember.biggest_pressure_area || null,
        notes: editMember.notes || null,
      };

      const response = await adminFetch(`/admin/members/${editingMember.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.success('Dad updated');
        setShowEditModal(false);
        setEditingMember(null);
        await fetchMembers();
      } else {
        toast.error(await readErrorResponse(response));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to update member');
    } finally {
      setSavingEdit(false);
    }
  }, [adminFetch, editMember, editingMember?.user_id, fetchMembers]);

  const handleQuickRelationshipStageUpdate = useCallback(async (member, relationshipStage) => {
    if (!member?.user_id) {
      toast.error('Missing user_id');
      return false;
    }

    const normalizedStage = relationshipStage || null;
    try {
      const response = await adminFetch(`/admin/members/${member.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationship_stage: normalizedStage }),
      });

      const data = response.ok ? await response.json().catch(() => ({})) : null;
      if (!response.ok) {
        const error = data?.detail || data?.message || response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
        return false;
      }

      const updatedUser = data?.user || null;
      const stageLabel = getDadStageDisplayLabel(normalizedStage) || 'Updated';
      toast.success(`${member.name || 'Dad'} marked ${stageLabel}`);

      if (updatedUser) {
        setMembers((previous) =>
          previous.map((entry) => (entry.user_id === updatedUser.user_id ? updatedUser : entry)),
        );
        setEditingMember((previous) =>
          previous?.user_id === updatedUser.user_id ? updatedUser : previous,
        );
      } else {
        await fetchMembers();
      }

      setEditMember((previous) =>
        previous ? { ...previous, relationship_stage: normalizedStage || '' } : previous,
      );
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to update relationship stage');
      return false;
    }
  }, [adminFetch, fetchMembers]);

  const handleMoveToGuidePartners = useCallback(async (member) => {
    if (!member?.user_id) {
      toast.error('Missing user_id');
      return false;
    }

    setMovingToGuidePartnerId(member.user_id);
    try {
      const response = await adminFetch(`/admin/members/${member.user_id}/move-to-guide-partners`, {
        method: 'POST',
      });
      const data = response.ok ? await response.json().catch(() => ({})) : null;

      if (!response.ok) {
        const error = data?.detail || data?.message || response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
        return false;
      }

      toast.success(`${member.name || 'Dad'} moved to Guide Partners`);
      setShowEditModal(false);
      setEditingMember(null);
      await fetchMembers();
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to move dad to Guide Partners');
      return false;
    } finally {
      setMovingToGuidePartnerId(null);
    }
  }, [adminFetch, fetchMembers]);

  const handleImportDads = useCallback(async (payload) => {
    setImportingDads(true);
    try {
      const response = await adminFetch('/admin/members/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = data?.detail || data?.message || response.statusText;
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
        return false;
      }

      const summary = `Imported ${data.imported_count} dads (${data.created_count} new, ${data.updated_count} updated)`;
      if (data.skipped_count > 0) {
        toast.success(`${summary}. Skipped ${data.skipped_count}.`);
      } else {
        toast.success(summary);
      }

      await fetchMembers();
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to import dads');
      return false;
    } finally {
      setImportingDads(false);
    }
  }, [adminFetch, fetchMembers]);

  return {
    searchTerm,
    setSearchTerm,
    members,
    membersLoading,
    showRolesModal,
    setShowRolesModal,
    showRemoveModal,
    setShowRemoveModal,
    memberToRemove,
    setMemberToRemove,
    removeReason,
    setRemoveReason,
    removing,
    showHardDeleteModal,
    setShowHardDeleteModal,
    memberToHardDelete,
    setMemberToHardDelete,
    hardDeleting,
    reinstatingId,
    selectedMember,
    savingRoles,
    memberRoles,
    setMemberRoles,
    showRemoved,
    setShowRemoved,
    showEditModal,
    setShowEditModal,
    editingMember,
    editMember,
    setEditMember,
    savingEdit,
    uploadingPhoto,
    movingToGuidePartnerId,
    editMemberPhotoInputRef,
    openEditModal,
    handleEditMemberPhotoUpload,
    fetchMembers,
    openRolesModal,
    toggleAdditionalRole,
    handleSaveRoles,
    importingDads,
    handleImportDads,
    openRemoveModal,
    openHardDeleteModal,
    handleRemoveMember,
    handleHardDeleteMember,
    handleReinstateMember,
    handleSaveMemberEdit,
    handleQuickRelationshipStageUpdate,
    handleMoveToGuidePartners,
  };
}
